import styles from './template.css';
import template from './template';
import {AoflElement, customElement, property} from '@aofl/element';
import {UnityAppCall} from '@aoflmkt/app-call';
import {validationMixin, isRequired} from '@aofl/form-validate';
import {uncaughtApiErrorsValidator, isValidEmail, isNotEmoji} from '@aoflmkt/form-validators';
import {isMobile} from '@aoflmkt/is-mobile';
import {isWebpQualifiedMixin} from '@aoflmkt/is-webp-qualified-mixin';
import {routerInstance} from '@aofl/router';
import {accessibilityMixin} from '../../../../../modules/accessibility-mixin';
import {AdjustService} from '../../../../../modules/adjust-service';
import {ApiService} from '../../../../../modules/api-service';
import {clickTrackerMixin} from '../../../../../modules/click-tracker-mixin';
import {pages, cookies, environment, appCallCallbacks, pixelEvents} from '../../../../../modules/constants-enumerate';
import {PixelService} from '../../../../../modules/pixel-service';
import {prospectRegFormSdo} from './modules/sdo';
import Cookies from 'js-cookie';
import {SentryService} from '../../../../../modules/sentry-service';

const Mixin = isWebpQualifiedMixin(validationMixin(clickTrackerMixin(accessibilityMixin(AoflElement))));

/**
 * @summary ProspectRegForm
 * @class HomeFooter
 * @extends {AoflElement}
 */
@customElement('prospect-reg-form')
class ProspectRegForm extends Mixin {
  /**
   * Creates an instance of ProspectRegForm.
   */
  constructor() {
    super();
    this.validators = this.defineValidators();
    this.regFormId = this.id + '-checkbox';
    this.emailId = this.id + '-email';
    this.formFieldId = this.id + '-id';
    this.isDesktop = !isMobile.any;
    this.placeholderText = (this.id === 'top-prospect-reg' || this.id === 'modal-prospect-reg' ? 'Email Address' : 'Enter your email address');
  }

  /**
   * @readonly
   */
  static is = 'prospect-reg-form';

  @property({type: String, attribute: false})
  email = '';
  @property({type: String, attribute: false})
  emailId = '';
  @property({type: Boolean, attribute: false, store: prospectRegFormSdo, mapState: 'checkbox'})
  checkbox
  @property({type: Object, attribute: false, store: prospectRegFormSdo, mapState: 'apiErrors'})
  apiErrors
  @property({type: String, attribute: true})
  'adjust-token' = '';


  /**
   * @private
   * @return {Object}
   */
  defineValidators() {
    return {
      email: {isRequired, isValidEmail, isNotEmoji},
      checkbox: {isChecked: () => this.checkbox},
      apiErrors: {
        uncaught: uncaughtApiErrorsValidator()
      }
    };
  }

  /**
   * @private
   */
  clearApiErrors() {
    if (this.apiErrors) {
      this.apiErrors = null;
      prospectRegFormSdo.apiErrors = null;
      this.form.apiErrors.validate();
    }
  }

  /**
   * @param {Event} e
   */
  updateEmail(e) {
    this.clearApiErrors();
    this.email = e.target.value;
  }

  /**
   *
   */
  validateEmail() {
    // empty string should trigger required, not invalid email
    if (this.email.length !== 0) {
      this.form.email.isValidEmail.validate();
      this.form.email.isNotEmoji.validate();
    }
    if (this.form.observed) this.form.email.validate();
  }

  /**
   * @param {Event} e
   */
  updateCheckbox(e) {
    this.checkbox = e.target.checked;
    prospectRegFormSdo.checkbox = e.target.checked;
    this.form.checkbox.validate();
  }

  /**
   * @param {Event} e
   */
  async submitForm(e) {
    e.preventDefault();
    const prospectRegForm = this.shadowRoot.getElementById(this.formFieldId);
    if (prospectRegForm.disabled) return;

    this.clearApiErrors();

    this.form.validate();
    await this.form.validateComplete;
    if (!this.form.valid) {
      this.regFormEmail = this.shadowRoot.getElementById(this.emailId);
      this.regFormEmail.focus();
      return;
    }

    prospectRegForm.disabled = true;

    try {
      await ApiService.prospectRegisterEmail(this.email, this.id);
      await PixelService.fire(pixelEvents.PROSPECT_REGISTRATION);

      Cookies.set(cookies.ELOG, this.email, cookies.CONFIG);

      if (isMobile.apple.device && environment.IN_APP && !environment.IS_DESKTOP) {
        const isActive = await ApiService.emailIsActiveUser(this.email);
        if (isActive.payload.is_active === true) {
          localStorage.setItem('isActive', isActive);
        }
      }

      const adjustToken = this['adjust-token'];
      AdjustService.track(adjustToken, this.adjustEventCallback.bind(this));

      routerInstance.navigate(pages.SUBSCRIPTION, {});
    } catch (err) {
      prospectRegForm.disabled = false;
      this.apiErrors = err.payload;
      prospectRegFormSdo.apiErrors = err.payload;
      SentryService.captureException(err);
      this.form.apiErrors.validate();
    }
  }

  /**
   *
   * @private
   * @param {Object} data
   * @return {Boolean}
   */
  adjustEventCallback(data) {
    if (typeof data !== 'object' || typeof data.request === 'undefined' || data.request !== appCallCallbacks.ADJUST_EVENT) {
      return false;
    }
    UnityAppCall.addListener(this.adjustEventCallback)();
    routerInstance.navigate(pages.SUBSCRIPTION, {});
  }

  /**
   *
   */
  firstUpdated() {
    super.firstUpdated();
    if (this.className.includes('modal-reg')) {
      const modalRegInput = this.shadowRoot.getElementById('top-prospect-reg-email');
      modalRegInput.focus();
    }
  }

  /**
   *
   */
  async connectedCallback() {
    super.connectedCallback();

    await this.updateComplete;
    this.checkboxElem = this.shadowRoot.getElementById('top-prospect-reg-email');
    this.checkboxElem.addEventListener('keydown', (e) => {
      if (e.keyCode === 13) {
        this.checkboxElem.click();
      }
    });
  }

  /**
   *
   * @return {Object}
   */
  render() {
    return super.render(template, [styles]);
  }
}

export default ProspectRegForm;
